import React, { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  PAGAMENTOS_INSCRITOS_LIGA_FILTRADOS,
  type PagamentosInscritosLigaFiltradosData,
  type PagamentosInscritosLigaFiltradosVars,
  criarBoletoInscritos,
} from '../../../services/pagamentoInscritosLigaService';
import type { PagamentoInscritosLiga } from '../../../types/pagamentoInscritosLiga';
import {
  type CategoriaTorneio,
  type InscricoesPorTorneioData,
} from '../../../services/torneioService';
import PaymentStatusChip from '../../../components/PaymentStatusChip';
import BoletoDetailDialog from '../../../components/liga/BoletoDetailDialog';
import { formatCurrency } from '../../../utils/money';

const COLUMN_COUNT = 7;

const formatDate = (value?: string | null) =>
  value ? (dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm') : '') : '';

interface TournamentChargesTabProps {
  torneioId: string;
  categorias: CategoriaTorneio[];
  inscricoes: InscricoesPorTorneioData['inscricoesTorneioPorAtletaIdTorneioId'];
  ligaId: string;
  canManagePayments: boolean;
}

const TournamentChargesTab: React.FC<TournamentChargesTabProps> = ({
  torneioId,
  categorias,
  inscricoes,
  ligaId,
  canManagePayments,
}) => {
  const [generatingMap, setGeneratingMap] = useState<Record<string, boolean>>({});
  const [selectedBoleto, setSelectedBoleto] = useState<PagamentoInscritosLiga | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const { data, loading, error, refetch } = useQuery<
    PagamentosInscritosLigaFiltradosData,
    PagamentosInscritosLigaFiltradosVars
  >(PAGAMENTOS_INSCRITOS_LIGA_FILTRADOS, {
    variables: { ligaId, size: 9999 },
    skip: !ligaId,
    fetchPolicy: 'no-cache',
  });

  const pagamentosMap = useMemo(() => {
    const allItems = data?.pagamentosInscritosLigaFiltrados?.items ?? [];
    const map = new Map<string, PagamentoInscritosLiga>();
    allItems.forEach((p) => {
      if (p.categoriaTorneio?.torneio?.id === torneioId) {
        map.set(p.categoriaTorneio.id, p);
      }
    });
    return map;
  }, [data, torneioId]);

  const getInscritosConfirmados = (categoriaId: string): number => {
    return inscricoes.filter(
      (i) =>
        i.categoriaTorneio?.id === categoriaId &&
        i.status === 'CONFIRMADA',
    ).length;
  };

  const handleGerarBoleto = async (categoriaTorneioId: string) => {
    setGeneratingMap((prev) => ({ ...prev, [categoriaTorneioId]: true }));
    try {
      await criarBoletoInscritos(categoriaTorneioId);
      await refetch();
      setSnackbar({ open: true, message: 'Boleto gerado com sucesso.', severity: 'success' });
    } catch (e: any) {
      const errorMessage = e?.message || 'Erro ao gerar boleto. Tente novamente.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setGeneratingMap((prev) => ({ ...prev, [categoriaTorneioId]: false }));
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#6c757d' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Categoria</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="center">
                Inscritos
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Valor</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status Pagamento</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Vencimento</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Ação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: COLUMN_COUNT }).map((__, cidx) => (
                    <TableCell key={cidx}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <div className="alert alert-danger mb-0" role="alert">
                    Erro ao carregar cobranças. Tente novamente.
                  </div>
                </TableCell>
              </TableRow>
            ) : categorias.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  Nenhuma categoria encontrada.
                </TableCell>
              </TableRow>
            ) : (
              categorias.map((cat) => {
                const pagamento = pagamentosMap.get(cat.id);
                const inscritosConfirmados = getInscritosConfirmados(cat.id);
                const isEncerrada = cat.status === 'ENCERRADA';
                const showGerarBoleto =
                  canManagePayments && isEncerrada && !pagamento;
                const isGenerating = generatingMap[cat.id] || false;

                return (
                  <TableRow key={cat.id}>
                    <TableCell>{cat.nome}</TableCell>
                    <TableCell>
                      <Chip label={cat.status || '-'} size="small" />
                    </TableCell>
                    <TableCell align="center">{inscritosConfirmados}</TableCell>
                    <TableCell>
                      {pagamento ? formatCurrency(pagamento.valor) : '-'}
                    </TableCell>
                    <TableCell>
                      {pagamento ? (
                        <PaymentStatusChip status={pagamento.status} />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {pagamento ? formatDate(pagamento.dataVencimento) : '-'}
                    </TableCell>
                    <TableCell>
                      {showGerarBoleto ? (
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          onClick={() => handleGerarBoleto(cat.id)}
                          disabled={isGenerating}
                          startIcon={
                            isGenerating ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : undefined
                          }
                        >
                          {isGenerating ? 'Gerando...' : 'Gerar Boleto'}
                        </Button>
                      ) : pagamento ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setSelectedBoleto(pagamento)}
                        >
                          Ver Boleto
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <BoletoDetailDialog
        open={selectedBoleto !== null}
        pagamento={selectedBoleto}
        onClose={() => setSelectedBoleto(null)}
      />
    </>
  );
};

export default TournamentChargesTab;
